import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle, Award, ShieldCheck } from "lucide-react";

export default function BundleDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/api/bundles/${slug}`).then(({data}) => setBundle(data))
      .catch(() => toast.error("Bundle not found"));
  }, [slug]);

  const buy = async () => {
    if (!user) { nav("/login", { state: { from: `/bundles/${slug}` } }); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/api/payments/checkout/session", {
        bundle_id: bundle.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (!bundle) return <div className="py-24 text-center">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="bundle-detail">
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="label-eyebrow text-[#D92D20]">Bundle · Save ${bundle.savings_usd?.toFixed(2)}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tight">{bundle.name}</h1>
          <p className="text-lg text-[#4B5563] mt-4 leading-relaxed">{bundle.description}</p>

          <h2 className="font-display font-bold text-2xl mt-10">What's included</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {bundle.courses.map((c) => (
              <div key={c.id} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden card-lift">
                <img src={c.thumbnail_url} alt={c.title} className="aspect-[16/9] w-full object-cover" />
                <div className="p-4">
                  <div className="font-display font-bold leading-tight">{c.title}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-[#4B5563] line-through">${c.price_usd}</span>
                    <CheckCircle className="w-4 h-4 text-[#064E3B]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-sm">
            <img src={bundle.thumbnail_url} alt={bundle.name} className="aspect-[16/9] w-full object-cover" />
            <div className="p-6">
              <div className="flex items-end justify-between">
                <div>
                  <div className="font-display font-black text-4xl">${bundle.price_usd?.toFixed(2)}</div>
                  <div className="text-sm text-[#4B5563] line-through">${bundle.original_total?.toFixed(2)}</div>
                </div>
                <div className="bg-[#064E3B] text-white text-xs font-bold uppercase tracking-widest px-2.5 py-1.5 rounded">Save ${bundle.savings_usd?.toFixed(2)}</div>
              </div>
              <button data-testid="bundle-buy" onClick={buy} disabled={paying} className="btn-primary w-full mt-5">
                {paying ? "Redirecting..." : "Buy Bundle"}
              </button>
              <ul className="mt-6 space-y-2 text-sm">
                <li className="flex items-center gap-2"><Award className="w-4 h-4 text-[#064E3B]" /> {bundle.courses.length} certificates included</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#064E3B]" /> Lifetime access</li>
                <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#064E3B]" /> Secure Stripe checkout</li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
